import React, { useState, useEffect } from "react";
import {
  Box, Drawer, Toolbar, List, ListItemButton, ListItemText, Typography,
  Button, IconButton, Modal, TextField, Paper, Table, TableHead, TableRow,
  TableBody, TableCell, ImageList, ImageListItem, CircularProgress,
  Snackbar, Alert
} from "@mui/material";

import { Add, Edit, Delete, Image, Close } from "@mui/icons-material";
import axios from "axios";

const drawerWidth = 260;

const AdminPanel = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", type: "success" });

  const [productModal, setProductModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [existingImages, setExistingImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);

  const [productData, setProductData] = useState({
    id: null, title: "", description: "", fulldescription: "",
    price: "", originalPrice: "", quantity: "", max_quantity: "",
    colors: "", details: "",
  });

  const [catModal, setCatModal] = useState(false);
  const [newCategory, setNewCategory] = useState({
    category: "", routing_category: ""
  });

  const [categoryImg, setCategoryImg] = useState(null);
  const [categoryPreview, setCategoryPreview] = useState(null);

const url = process.env.REACT_APP_API_URL || "https://api.durgaenterpriseglobal.com";

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${url}/api`);
      if (res.data.success) setCategories(res.data.data);
    } catch (e) {}
    setLoading(false);
  };

  const handleCatImg = (e) => {
    const file = e.target.files[0];
    setCategoryImg(file);
    setCategoryPreview(URL.createObjectURL(file));
  };

  const saveCategory = async () => {
    if (!newCategory.category || !newCategory.routing_category)
      return showToast("Enter category & routing category", "error");

    const fd = new FormData();
    fd.append("category", newCategory.category);
    fd.append("routing_category", newCategory.routing_category);
    if (categoryImg) fd.append("category_img", categoryImg);

    setLoading(true);
    try {
      await axios.post(`${url}/admin/category/add`, fd);
      showToast("Category Created Successfully", "success");
      setCatModal(false);
      setCategoryImg(null);
      setCategoryPreview(null);
      setNewCategory({ category: "", routing_category: "" });
      fetchCategories();
    } catch {
      showToast("Category Creation Failed", "error");
    }
    setLoading(false);
  };

  const deleteCategory = async () => {
    if (!window.confirm("Delete this category permanently?")) return;

    setLoading(true);
    try {
      await axios.delete(`${url}/admin/category/${selectedCategory.routing_category}`);
      showToast("Category Deleted", "success");
      setSelectedCategory(null);
      fetchCategories();
    } catch {
      showToast("Delete Failed", "error");
    }
    setLoading(false);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(files);
    setImagePreview(files.map((f) => URL.createObjectURL(f)));
  };

  const removePreviewImage = (index) => {
    setImagePreview((p) => p.filter((_, i) => i !== index));
    setImageFiles((p) => p.filter((_, i) => i !== index));
  };

  const openEditModal = (p) => {
    setEditMode(true);
    setProductModal(true);
    setExistingImages(p.images);
    setProductData({
      id: p.id,
      title: p.title,
      description: p.description,
      fulldescription: p.fulldescription,
      price: p.price.current,
      originalPrice: p.price.original,
      quantity: p.quantity,
      max_quantity: p.max_quantity,
      colors: p.colors.join(", "),
      details: JSON.stringify(p.details, null, 2),
    });
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete product permanently?")) return;

    setLoading(true);
    try {
      await axios.delete(`${url}/admin/product/${selectedCategory.category}/${id}`);
      showToast("Product Deleted", "success");
      fetchCategories();
    } catch {
      showToast("Delete Failed", "error");
    }
    setLoading(false);
  };

  const deleteExistingImage = async (index) => {
    setLoading(true);
    try {
      await axios.delete(`${url}/admin/product/${productData.id}/image/${index}`);
      showToast("Image Removed", "success");
      const updated = existingImages.filter((_, i) => i !== index);
      setExistingImages(updated);
      fetchCategories();
    } catch {
      showToast("Image Delete Failed", "error");
    }
    setLoading(false);
  };

  const saveProduct = async () => {
    const fd = new FormData();
    Object.keys(productData).forEach((k) => fd.append(k, productData[k]));
    fd.append("categoryName", selectedCategory.category);
    imageFiles.forEach((file) => fd.append("images", file));

    const endpoint = editMode
      ? `${url}/admin/product/${productData.id}`
      : `${url}/admin/product/add`;
    const method = editMode ? "put" : "post";

    setLoading(true);
    try {
      await axios({ method, url: endpoint, data: fd });
      showToast(editMode ? "Updated Successfully" : "Product Created", "success");
      resetModal();
      fetchCategories();
    } catch {
      showToast("Save Failed", "error");
    }
    setLoading(false);
  };

  const resetModal = () => {
    setProductModal(false);
    setEditMode(false);
    setImageFiles([]);
    setImagePreview([]);
  };

  const showToast = (message, type = "success") =>
    setToast({ open: true, message, type });

  return (
    <Box sx={{ display: "flex" }}>

      {loading && (
        <Box sx={{
          position: "fixed", inset: 0, bgcolor: "rgba(0,0,0,0.4)",
          display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999
        }}>
          <CircularProgress size={70} sx={{ color: "#00E5FF" }} />
        </Box>
      )}

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            background: "#0A0F24",
            color: "#fff",
            borderRight: "1px solid #222",
          },
        }}
      >
        <Toolbar />
        <Box sx={{ p: 2 }}>
          <Typography variant="h5" fontWeight="bold" color="white">Admin Panel</Typography>
        </Box>

        <List>
          {categories.map((cat) => (
            <ListItemButton
              key={cat.id}
              onClick={() => setSelectedCategory(cat)}
              sx={{
                borderLeft: selectedCategory?.id === cat.id ? "4px solid #00E5FF" : "none",
                bgcolor: selectedCategory?.id === cat.id ? "rgba(0,229,255,0.15)" : "",
              }}
            >
              <ListItemText primary={cat.category} />
            </ListItemButton>
          ))}
        </List>

        <Box sx={{ p: 2 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<Add />}
            onClick={() => setCatModal(true)}
            sx={{ bgcolor: "#00E5FF", color: "black", fontWeight: "bold" }}
          >
            Add Category
          </Button>
        </Box>
      </Drawer>

      <Box sx={{ flexGrow: 1, p: 3 }}>
        {!selectedCategory ? (
          <Box
            sx={{
              textAlign: "center",
              mt: 10,
              opacity: 0.95,
              display: "flex",
              flexDirection: "column",
              alignItems: "center"
            }}
          >
            <img
              src="https://cdn-icons-png.flaticon.com/512/3597/3597075.png"
              alt="welcome"
              style={{ width: 160, marginBottom: 20 }}
            />
            <Typography variant="h3" fontWeight="bold" mb={1}>
              Welcome Admin 👋
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 600, mb: 3 }}>
              Select a category to manage products or create a new one to begin.
            </Typography>

            <Button
              variant="contained"
              sx={{ bgcolor: "#00E5FF", color: "#000", fontWeight: "bold", px: 4 }}
              startIcon={<Add />}
              onClick={() => setCatModal(true)}
            >
              Create Category
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3
            }}>
              <Typography variant="h4" fontWeight="bold">
                {selectedCategory.category}
              </Typography>

              <Box>
                <Button
                  variant="outlined"
                  sx={{ mr: 2, borderColor: "#00E5FF", color: "#00E5FF" }}
                  startIcon={<Edit />}
                  onClick={() => setCatModal(true)}
                >
                  Edit Category
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Delete />}
                  onClick={deleteCategory}
                >
                  Delete Category
                </Button>
              </Box>
            </Box>

            <Paper sx={{ borderRadius: 3 }}>
              <Table>
                <TableHead sx={{ bgcolor: "#E9EEF7" }}>
                  <TableRow>
                    <TableCell>Image</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {selectedCategory.products.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        <img src={p.image} style={{ width: 60, borderRadius: 8 }} alt="" />
                      </TableCell>
                      <TableCell>{p.title}</TableCell>
                      <TableCell>₹{p.price.current}</TableCell>
                      <TableCell>{p.quantity}</TableCell>
                      <TableCell>
                        <IconButton onClick={() => openEditModal(p)}><Edit /></IconButton>
                        <IconButton color="error" onClick={() => deleteProduct(p.id)}><Delete /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setProductModal(true)}
                sx={{ bgcolor: "#00E5FF", color: "#000", fontWeight: "bold" }}
              >
                Add Product
              </Button>
            </Box>
          </>
        )}
      </Box>

      {/* CATEGORY MODAL */}
      <Modal open={catModal} onClose={() => setCatModal(false)}>
        <Box sx={{
          maxWidth: 500, width: "90%", background: "white",
          margin: "100px auto", padding: 4, borderRadius: 4
        }}>
          <Typography variant="h5" fontWeight="bold" mb={2}>Category</Typography>

          <TextField fullWidth label="Category Name" sx={{ mb: 2 }}
            value={newCategory.category}
            onChange={(e) => setNewCategory({ ...newCategory, category: e.target.value })}
          />
          <TextField fullWidth label="Routing Category" sx={{ mb: 2 }}
            value={newCategory.routing_category}
            onChange={(e) => setNewCategory({ ...newCategory, routing_category: e.target.value })}
          />

          <Button variant="outlined" fullWidth component="label" sx={{ mb: 2 }} startIcon={<Image />}>
            Upload Category Image
            <input hidden type="file" accept="image/*" onChange={handleCatImg} />
          </Button>

          {categoryPreview && (
            <Box sx={{ textAlign: "center", mb: 2, position: "relative", display: "inline-block" }}>
              <img src={categoryPreview} alt="" style={{ width: 150, borderRadius: 10 }} />
              <IconButton
                onClick={() => {
                  setCategoryPreview(null);
                  setCategoryImg(null);
                }}
                sx={{
                  position: "absolute",
                  top: -10,
                  right: -10,
                  bgcolor: "white",
                  border: "1px solid #ddd"
                }}
              >
                <Close />
              </IconButton>
            </Box>
          )}

          <Button variant="contained" fullWidth sx={{ bgcolor: "#00E5FF", color: "#000" }}
            onClick={saveCategory}
          >
            Save Category
          </Button>
        </Box>
      </Modal>

      {/* PRODUCT MODAL */}
      <Modal open={productModal} onClose={resetModal}>
        <Box sx={{
          maxWidth: 950, width: "90%", background: "white",
          margin: "20px auto", padding: 4, borderRadius: 4,
          maxHeight: "90vh", overflowY: "auto"
        }}>
          <Typography variant="h4" mb={2} fontWeight="bold">
            {editMode ? "Edit Product" : "Add Product"}
          </Typography>

          <TextField fullWidth label="Title" sx={{ mb: 2 }}
            value={productData.title}
            onChange={(e) => setProductData({ ...productData, title: e.target.value })}
          />

          <TextField fullWidth label="Short Description" sx={{ mb: 2 }}
            value={productData.description}
            onChange={(e) => setProductData({ ...productData, description: e.target.value })}
          />

          <TextField fullWidth multiline rows={4} label="Full Description" sx={{ mb: 2 }}
            value={productData.fulldescription}
            onChange={(e) => setProductData({ ...productData, fulldescription: e.target.value })}
          />

          <TextField fullWidth label="Price" type="number" sx={{ mb: 2 }}
            value={productData.price}
            onChange={(e) => setProductData({ ...productData, price: e.target.value })}
          />

          <TextField fullWidth label="Original Price" type="number" sx={{ mb: 2 }}
            value={productData.originalPrice}
            onChange={(e) => setProductData({ ...productData, originalPrice: e.target.value })}
          />

          <TextField fullWidth label="Quantity" type="number" sx={{ mb: 2 }}
            value={productData.quantity}
            onChange={(e) => setProductData({ ...productData, quantity: e.target.value })}
          />

          <TextField fullWidth label="Max Quantity" type="number" sx={{ mb: 2 }}
            value={productData.max_quantity}
            onChange={(e) => setProductData({ ...productData, max_quantity: e.target.value })}
          />

          <TextField fullWidth label="Colors (comma separated)" sx={{ mb: 2 }}
            value={productData.colors}
            onChange={(e) => setProductData({ ...productData, colors: e.target.value })}
          />

          <TextField fullWidth label="Details JSON" multiline rows={6} sx={{ mb: 2 }}
            value={productData.details}
            onChange={(e) => setProductData({ ...productData, details: e.target.value })}
          />

          {editMode && (
            <>
              <Typography variant="h6">Existing Images</Typography>
              <ImageList cols={4} sx={{ my: 2 }}>
                {existingImages.map((img, i) => (
                  <ImageListItem key={i}>
                    <img src={img} alt="" />
                    <IconButton
                      onClick={() => deleteExistingImage(i)}
                      sx={{ position: "absolute", top: 5, right: 5, bgcolor: "white" }}
                    ><Close /></IconButton>
                  </ImageListItem>
                ))}
              </ImageList>
            </>
          )}

          <Button variant="outlined" fullWidth component="label" startIcon={<Image />}>
            Upload Images
            <input hidden multiple accept="image/*" type="file" onChange={handleFileSelect} />
          </Button>

          <ImageList cols={4} sx={{ mt: 2 }}>
            {imagePreview.map((src, i) => (
              <ImageListItem key={i}>
                <img src={src} alt="" />
                <IconButton
                  onClick={() => removePreviewImage(i)}
                  sx={{ position: "absolute", top: 5, right: 5, bgcolor: "white" }}
                ><Close /></IconButton>
              </ImageListItem>
            ))}
          </ImageList>

          <Button variant="contained" fullWidth
            sx={{ mt: 2, bgcolor: "#00E5FF", color: "#000" }}
            onClick={saveProduct}
          >
            {editMode ? "Update Product" : "Create Product"}
          </Button>
        </Box>
      </Modal>

      <Snackbar open={toast.open} autoHideDuration={2000}
        onClose={() => setToast({ ...toast, open: false })}
      >
        <Alert severity={toast.type} variant="filled">{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPanel;
